import { SiloArtCanvas } from "@/components/SiloArtLayer";
// BattlesHub — the main /wars landing page.
// Shows: Upcoming • Live • Completed tabs, plus a "Start a battle"
// button (gated to owners with at least one silo + BYOK).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sword, Trophy, Hourglass, ArrowLeft, Play, Search, Trash2, CheckSquare, Square, RefreshCw, Lock } from "lucide-react";
import { listBattles, deleteBattle, bulkDeleteBattles } from "./api";
import EloTrendChart from "./EloTrendChart";
import ChallengeModal from "./ChallengeModal";
import { useViewerSurface } from "@/lib/useViewerSurface";
import { useEntitlements } from "@/lib/entitlements";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { useBattlesEnabled } from "./useBattlesEnabled";
import { api } from "@/lib/api";
import { toast } from "sonner";
import LLMGuardWrap from "@/components/LLMGuardWrap";
import { useTrialKeepPrompt } from "@/lib/trial";

const PENDING_INVITE_TOKEN_KEY = "battle_pending_invite_token";

const TAB_FILTERS = [
  { id: "live", label: "Live now", icon: Play, color: "#C16E5A" },
  { id: "upcoming", label: "Upcoming", icon: Hourglass, color: "#A89360" },
  { id: "completed", label: "Past battles", icon: Trophy, color: "#4A5D4E" },
  { id: "search", label: "Find opponents", icon: Search, color: "#1C1B1A" },
];

export default function BattlesHub() {
  useTrialKeepPrompt("silo_wars");
  const [tab, setTab] = useState("live");
  const [battles, setBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChallenge, setShowChallenge] = useState(false);
  // Feb 14, 2026 PM — Past Battles cleanup. Selection state for bulk
  // delete. Only meaningful when tab === "completed".
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const nav = useNavigate();
  const { isOperator, user } = useViewerSurface();
  // Jul 2026 (Randy) — Silo Wars is now a paid Creator Pro unlock (`silo_wars`
  // SKU). Only owners can INITIATE; everyone else who's an operator sees a
  // clean "Unlock Silo Wars" nudge (opens the buffet) instead of hitting a 403.
  const { owns } = useEntitlements();
  const { openGate } = useCreatorProGate();
  const canInitiate = isOperator && owns("silo_wars");
  const handleChallenge = (silo) => {
    if (canInitiate) setShowChallenge(silo ?? true);
    else openGate("silo_wars");
  };
  const { enabled: battlesEnabled, loading: flagLoading } = useBattlesEnabled();

  // Master-toggle hard gate. When OFF, redirect quietly out — the
  // feature should be 100% invisible per Randy's spec.
  useEffect(() => {
    if (!flagLoading && battlesEnabled === false) {
      nav(isOperator ? "/dashboard" : "/fan-portal", { replace: true });
    }
  }, [flagLoading, battlesEnabled, isOperator]);

  // Feb 14, 2026 PM session #5 — track when we last successfully pulled
  // data. Shown next to the Refresh button as "Last refreshed: 23s ago"
  // so users know how stale the snapshot is at a glance. Resets per tab.
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  const loadBattles = async (filter) => {
    setLoading(true);
    try {
      // Feb 14, 2026 PM — Past Battles is now strictly user-scoped. Other
      // tabs (Live/Upcoming) stay platform-wide for discoverability.
      const data = await listBattles(filter, { mineOnly: filter === "completed" });
      setBattles(data);
      setLastRefreshedAt(Date.now());
    } catch (e) {
      console.error("Failed to load battles", e);
    } finally {
      setLoading(false);
    }
  };

  // Tick the clock every 5s so the "Xs ago" label feels alive without
  // hammering re-renders. Pauses when the tab isn't visible.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") setNow(Date.now());
    }, 5000);
    return () => clearInterval(t);
  }, []);

  // Clear selection whenever we switch tabs or the user reloads.
  useEffect(() => { setSelectedIds(new Set()); setLastRefreshedAt(null); }, [tab]);

  // Derived: contestant-only battle ids visible on the Past Battles tab.
  // Used by Select-All + bulk delete. Memoized so the toolbar doesn't
  // thrash on every keystroke.
  const completedIds = useMemo(
    () => (tab === "completed" ? battles.map((b) => b.id) : []),
    [tab, battles],
  );

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      // If everything's already selected, clear. Otherwise select all.
      if (prev.size === completedIds.length) return new Set();
      return new Set(completedIds);
    });
  };

  const onDeleteOne = async (id) => {
    setDeleting(true);
    try {
      await deleteBattle(id);
      setBattles((prev) => prev.filter((b) => b.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success("Removed from your archive.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove this battle.");
    } finally {
      setDeleting(false);
    }
  };

  const onDeleteSelected = async () => {
    if (!selectedIds.size) return;
    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await bulkDeleteBattles(ids);
      setBattles((prev) => prev.filter((b) => !selectedIds.has(b.id)));
      setSelectedIds(new Set());
      const removed = r.deleted || 0;
      if (r.skipped > 0) {
        toast(`Removed ${removed} from your archive. Skipped ${r.skipped}.`);
      } else {
        toast.success(`Removed ${removed} from your archive.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk remove failed.");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (tab === "search") return; // search tab handles its own loading
    loadBattles(tab);
  }, [tab]);

  // Auto-prune stale upcoming rows opportunistically on hub mount so the
  // Upcoming tab never shows expired invites. Fire-and-forget — failures
  // are non-blocking.
  useEffect(() => {
    if (!battlesEnabled) return;
    api.post("/battles/maintenance/prune-stale-upcoming", {}).catch(() => {});
  }, [battlesEnabled]);

  // Post-signup auto-redeem: if a pending invite token is in localStorage
  // (left there by InviteRedeemPage) and the user is now authed, redeem
  // it on /wars page-load. Best-effort.
  useEffect(() => {
    if (!user) return;
    let token = null;
    try { token = localStorage.getItem(PENDING_INVITE_TOKEN_KEY); } catch { /* ignore */ }
    if (!token) return;
    (async () => {
      try {
        await api.post(`/battles/invite/${token}/redeem`, {});
        toast.success("Battle invite redeemed. Build your silo to accept.");
      } catch { /* silent — token might be stale */ }
      try { localStorage.removeItem(PENDING_INVITE_TOKEN_KEY); } catch { /* ignore */ }
    })();
  }, [user?.id]);

  // Feb 14, 2026 PM session #4 — Killed the 5-second polling per user feedback.
  // Was: setInterval(loadBattles("live"), 5000) — 12 DB hits/min per viewer.
  // Now: load once when tab opens (see useEffect above). User refreshes via the
  // "Refresh" button rendered in the header.

  return (
    <div className="min-h-screen bg-[#F5F2ED] relative isolate" data-testid="battles-hub">
      <SiloArtCanvas />
      {/* Header */}
      <header className="border-b border-[#D8D6D2] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => nav(isOperator ? "/dashboard" : "/fan-portal")}
            className="flex items-center gap-2 text-[#1C1B1A] hover:opacity-70 transition"
            data-testid="battles-back-btn"
          >
            <ArrowLeft size={18} />
            <span className="text-sm">Back</span>
          </button>
          <div className="flex items-center gap-2">
            <Sword size={22} className="text-[#C16E5A]" />
            <h1 className="text-xl font-semibold tracking-tight text-[#1C1B1A]">Silo Wars</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/wars/hall")}
              className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1.5"
              data-testid="battles-hall-link"
            >
              🏆 Hall
            </button>
            {isOperator ? (
              canInitiate ? (
                <LLMGuardWrap surface="battle_run">
                  <button
                    onClick={() => setShowChallenge(true)}
                    className="px-4 py-2 bg-[#1C1B1A] text-white text-sm rounded-md hover:bg-[#2C2B2A] transition"
                    data-testid="battles-start-btn"
                  >
                    Start a battle
                  </button>
                </LLMGuardWrap>
              ) : (
                <button
                  onClick={() => openGate("silo_wars")}
                  className="px-4 py-2 bg-[#A89360] text-white text-sm rounded-md hover:bg-[#96844f] transition flex items-center gap-1.5"
                  data-testid="battles-unlock-btn"
                  title="Silo Wars is a Creator Pro capability"
                >
                  <Lock size={14} /> Unlock Silo Wars
                </button>
              )
            ) : <div className="w-[110px]" />}
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-3">
          <div className="flex gap-2 items-center">
            {TAB_FILTERS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition ${active ? "bg-[#1C1B1A] text-white" : "bg-white border border-[#D8D6D2] text-[#1C1B1A] hover:bg-[#FAFAFA]"}`}
                  data-testid={`battles-tab-${t.id}`}
                >
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
            {/* Feb 14, 2026 PM session #4 — Manual refresh replaces the
                old 5s polling. Lighter on the DB, calmer in the UI. Only
                meaningful on the data tabs (Live / Upcoming / Past).
                Session #5 — added "Last refreshed Xs ago" timestamp so
                users can gauge data freshness at a glance. */}
            {tab !== "search" && (
              <div className="ml-auto flex items-center gap-2">
                {lastRefreshedAt && (
                  <span className="text-[10px] text-[#6B6A68] tabular-nums" data-testid="battles-last-refreshed">
                    {formatRefreshAge(now - lastRefreshedAt)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => loadBattles(tab)}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-white border border-[#D8D6D2] text-[#6B6A68] hover:bg-[#FAFAFA] hover:text-[#1C1B1A] disabled:opacity-50"
                  data-testid="battles-refresh-btn"
                  title="Pull the latest"
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  Refresh
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        {tab === "search" ? (
          <OpponentSearchTab onChallenge={(silo) => handleChallenge(silo || true)} canChallenge={isOperator} />
        ) : loading ? (
          <div className="text-center py-20 text-[#6B6A68] text-sm">Loading…</div>
        ) : battles.length === 0 ? (
          <EmptyState tab={tab} canChallenge={isOperator} onChallenge={() => handleChallenge()} />
        ) : (
          <div className="space-y-3">
            {/* Past Battles cleanup toolbar — only shows when there's
                something to act on. Select-all toggles between empty +
                full selection; the delete button stays disabled until at
                least one row is checked. Feb 14, 2026 PM. */}
            {tab === "completed" && (
              <div className="flex items-center gap-2 px-2 py-2 bg-[#FAF7F2] border border-[#E8E5DF] rounded-md text-xs" data-testid="past-battles-toolbar">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="flex items-center gap-1.5 text-[#1C1B1A] hover:text-[#4A5D4E]"
                  data-testid="past-battles-select-all"
                >
                  {selectedIds.size === completedIds.length && completedIds.length > 0 ? (
                    <CheckSquare size={14} />
                  ) : (
                    <Square size={14} />
                  )}
                  {selectedIds.size === completedIds.length && completedIds.length > 0 ? "Unselect all" : "Select all"}
                </button>
                <span className="text-[#6B6A68]">·</span>
                <span className="text-[#6B6A68]">{selectedIds.size} selected</span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onDeleteSelected}
                    disabled={!selectedIds.size || deleting}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition ${
                      selectedIds.size && !deleting
                        ? "bg-[#A35757] text-white hover:bg-[#8A4747]"
                        : "bg-[#E5E2DD] text-[#6B6A68] cursor-not-allowed"
                    }`}
                    data-testid="past-battles-bulk-delete"
                    title="Delete selected from your archive. Other contestant's view is unaffected."
                  >
                    <Trash2 size={12} />
                    Delete from my archive
                  </button>
                </div>
              </div>
            )}

            {battles.map(b => (
              tab === "completed" ? (
                <PastBattleRow
                  key={b.id}
                  battle={b}
                  selected={selectedIds.has(b.id)}
                  onToggle={() => toggleSelected(b.id)}
                  onOpen={() => nav(`/wars/arena/${b.id}`)}
                  onDelete={() => onDeleteOne(b.id)}
                  busy={deleting}
                />
              ) : (
                <BattleCard key={b.id} battle={b} onOpen={() => nav(`/wars/arena/${b.id}`)} />
              )
            ))}
          </div>
        )}
      </main>

      {showChallenge && (
        <ChallengeModal
          onClose={() => setShowChallenge(false)}
          onCreated={(battle) => {
            setShowChallenge(false);
            nav(`/wars/arena/${battle.id}`);
          }}
          initialTargetSiloId={typeof showChallenge === "string" ? showChallenge : null}
        />
      )}
    </div>
  );
}

// Find opponents by category — searches the eligible silo pool, sorted by
// Elo desc. Auto-fetches counts so the picker shows live availability.
function OpponentSearchTab({ onChallenge, canChallenge }) {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCats, setLoadingCats] = useState(true);

  useEffect(() => {
    (async () => {
      setLoadingCats(true);
      try {
        const r = await api.get("/battles/silos/categories");
        setCategories(r.data?.categories || []);
        // Auto-pick the highest-count non-zero bucket so users see results immediately.
        const sorted = [...(r.data?.categories || [])].sort((a, b) => (b.count || 0) - (a.count || 0));
        const first = sorted.find((c) => (c.count || 0) > 0) || sorted[0];
        if (first) setSelectedCat(first.id);
      } catch {
        toast.error("Could not load categories.");
      } finally { setLoadingCats(false); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedCat) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/battles/silos/search-by-category?category=${encodeURIComponent(selectedCat)}&limit=30`);
        setResults(r.data?.silos || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Search failed.");
      } finally { setLoading(false); }
    })();
  }, [selectedCat]);

  if (loadingCats) return <div className="text-center py-20 text-[#6B6A68] text-sm">Loading categories…</div>;

  return (
    <div className="space-y-5" data-testid="opponent-search-tab">
      <div className="bg-white border border-[#D8D6D2] rounded-lg p-4">
        <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-2">
          Search opponents by category
        </label>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => {
            const active = c.id === selectedCat;
            const disabled = c.count === 0;
            return (
              <button
                key={c.id}
                onClick={() => !disabled && setSelectedCat(c.id)}
                disabled={disabled}
                data-testid={`opponent-cat-${c.id}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  active
                    ? "bg-[#1C1B1A] text-white border-[#1C1B1A]"
                    : disabled
                      ? "bg-[#F0F1EE] text-[#A89360] border-[#E5E5E0] cursor-not-allowed"
                      : "bg-white text-[#1C1B1A] border-[#D8D6D2] hover:border-[#1C1B1A]"
                }`}
              >
                {c.label} <span className="opacity-60 ml-1">{c.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6B6A68] text-sm">Searching…</div>
      ) : results.length === 0 ? (
        <div className="text-center py-12 text-[#6B6A68] text-sm bg-white border border-[#D8D6D2] rounded-lg">
          No eligible opponents in this category yet. As more creators set their silo categories and connect BYOK, this list fills up.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="opponent-search-results">
          {results.map((s) => (
            <div key={s.id} className="bg-white border border-[#D8D6D2] rounded-lg p-4 hover:border-[#C16E5A] transition" data-testid={`opponent-card-${s.slug}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#1C1B1A] truncate">{s.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">by {s.owner_name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#A89360]">{s.tier}</div>
                  <div className="text-sm font-mono tabular-nums text-[#1C1B1A]">Elo {s.elo}</div>
                </div>
              </div>
              {s.tagline && (
                <p className="text-xs text-[#6B6A68] line-clamp-2 mt-1 mb-3">{s.tagline}</p>
              )}
              {/* Feb 14, 2026 PM session #5 — ELO trend sparkline. Tiny
                  inline chart of this silo's last-30-day ELO trajectory.
                  No-ops gracefully when the silo hasn't battled. */}
              <div className="mb-3" data-testid={`opponent-trend-${s.slug}`}>
                <EloTrendChart siloId={s.id} days={30} width={320} height={48} showSummary={false} />
              </div>
              <div className="flex items-center justify-between text-[11px] text-[#6B6A68]">
                <span>
                  {s.record?.wins || 0}W · {s.record?.losses || 0}L · {s.record?.draws || 0}D
                </span>
                {canChallenge ? (
                  <button
                    onClick={() => onChallenge(s.id)}
                    data-testid={`opponent-challenge-${s.slug}`}
                    className="text-xs px-3 py-1.5 bg-[#C16E5A] text-white rounded-md hover:bg-[#A85E4A]"
                  >
                    Challenge →
                  </button>
                ) : (
                  <span className="text-[10px] italic text-[#A89360]">Build a silo to challenge</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ tab, canChallenge, onChallenge }) {
  const messages = {
    live: "Nothing going on right now. Be the first to throw down.",
    upcoming: "No challenges queued up.",
    completed: "No battles in the books yet.",
  };
  return (
    <div className="bg-white border border-[#D8D6D2] rounded-lg p-12 text-center" data-testid="battles-empty-state">
      <Sword size={32} className="mx-auto text-[#A89360] mb-3" />
      <p className="text-[#1C1B1A] mb-4">{messages[tab]}</p>
      {canChallenge && tab !== "completed" && (
        <button
          onClick={onChallenge}
          className="px-4 py-2 bg-[#1C1B1A] text-white text-sm rounded-md hover:bg-[#2C2B2A]"
          data-testid="battles-empty-challenge-btn"
        >
          Start one now
        </button>
      )}
    </div>
  );
}

// Feb 14, 2026 PM session #5 — friendly "X ago" formatter for the
// Refresh timestamp. <60s: seconds. <60min: minutes. >=60min: hours.
function formatRefreshAge(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function BattleCard({ battle, onOpen }) {
  const { challenger_silo: l, target_silo: r, state, topic, winner_silo_id, is_draw, elo_changes } = battle;
  const isLive = state === "live";
  const isReady = state === "ready";
  const isScheduled = state === "scheduled";
  const isFinished = state === "finished";
  const isPrivate = battle.is_broadcast === false;
  const currentRound = (battle.current_round_index ?? 0) + 1;
  const totalRounds = battle.question_count || (battle.rounds || []).length || 5;
  const reviewSeconds = battle.inter_round_review_seconds || 60;

  return (
    <button
      onClick={onOpen}
      className="w-full bg-white border border-[#D8D6D2] rounded-lg p-4 hover:shadow-md transition text-left"
      data-testid={`battle-card-${battle.id}`}
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {isLive && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C16E5A]/15 text-[#C16E5A] text-[11px] uppercase tracking-wider" data-testid="battle-card-live-pill">
              ● Live · Round {currentRound}/{totalRounds}
              <LiveRoundTimer battle={battle} />
            </span>
          )}
          {isReady && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#A89360]/15 text-[#A89360] text-[11px] uppercase tracking-wider">Starting soon</span>}
          {isScheduled && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#A89360]/15 text-[#A89360] text-[11px] uppercase tracking-wider">⏱ {battle.scheduled_for ? new Date(battle.scheduled_for).toLocaleString() : "Scheduled"}</span>}
          {isFinished && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4A5D4E]/15 text-[#4A5D4E] text-[11px] uppercase tracking-wider">Finished</span>}
          {isPrivate && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1C1B1A]/8 text-[#6B6A68] text-[11px] uppercase tracking-wider">🔒 Private</span>}
          {battle.is_rehearsal && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#1C1B1A]/8 text-[#6B6A68] text-[11px] uppercase tracking-wider">Rehearsal</span>}
          {(isReady || isScheduled) && (
            <span className="text-[10px] text-[#6B6A68] uppercase tracking-wider">
              Review window: {reviewSeconds}s
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#6B6A68]">{new Date(battle.created_at).toLocaleString()}</div>
      </div>
      {topic && (
        <div className="mb-3 text-sm text-[#1C1B1A] italic" data-testid="battle-card-topic">
          "{topic}"
        </div>
      )}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
        <SiloChip silo={l} winner={isFinished && !is_draw && winner_silo_id === l?.id} delta={elo_changes?.[l?.id]?.delta} align="left" />
        <span className="text-[#A89360] text-xs uppercase tracking-widest font-semibold">vs</span>
        <SiloChip silo={r} winner={isFinished && !is_draw && winner_silo_id === r?.id} delta={elo_changes?.[r?.id]?.delta} align="right" />
      </div>
      {/* Feb 14, 2026 PM session #6 — surface BOTH categories on the Live tab
          row so spectators can see at a glance what the matchup is about. */}
      {(l?.category || r?.category) && (
        <div className="mt-3 flex items-center gap-2 text-[10px] text-[#6B6A68] uppercase tracking-[0.14em]">
          {l?.category && (
            <span className="px-1.5 py-0.5 bg-[#F0EDE7] rounded">{l.category}</span>
          )}
          <span className="opacity-40">·</span>
          {r?.category && (
            <span className="px-1.5 py-0.5 bg-[#F0EDE7] rounded">{r.category}</span>
          )}
        </div>
      )}
    </button>
  );
}

// Live round countdown ticker — shows mm:ss when battle is in the
// gated review window, "thinking…" otherwise. Feb 14, 2026 PM session #6.
function LiveRoundTimer({ battle }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (battle.current_round_state !== "gated" || !battle.gated_round_started_at) return null;
  const startedAt = new Date(battle.gated_round_started_at).getTime();
  const total = (battle.inter_round_review_seconds || 60) * 1000;
  const remaining = Math.max(0, total - (now - startedAt));
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000);
  return <span className="font-mono tabular-nums">· {mm}:{String(ss).padStart(2, "0")}</span>;
}

// Past-battles row — wraps BattleCard with a checkbox (bulk select) +
// a trash button (individual delete). Feb 14, 2026 PM.
function PastBattleRow({ battle, selected, onToggle, onOpen, onDelete, busy }) {
  return (
    <div
      className={`flex items-stretch gap-2 rounded-lg transition ${selected ? "ring-2 ring-[#A35757]/40" : ""}`}
      data-testid={`past-battle-row-${battle.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="shrink-0 w-10 flex items-center justify-center bg-white border border-[#D8D6D2] rounded-l-lg hover:bg-[#FAFAFA]"
        data-testid={`past-battle-toggle-${battle.id}`}
        aria-label={selected ? "Unselect this battle" : "Select this battle"}
      >
        {selected ? <CheckSquare size={16} className="text-[#A35757]" /> : <Square size={16} className="text-[#6B6A68]" />}
      </button>
      <div className="flex-1 min-w-0 [&>button]:rounded-none [&>button]:border-l-0 [&>button]:border-r-0">
        <BattleCard battle={battle} onOpen={onOpen} />
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="shrink-0 w-10 flex items-center justify-center bg-white border border-[#D8D6D2] rounded-r-lg text-[#A35757] hover:bg-[#FBE8E8] hover:border-[#A35757] disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`past-battle-delete-${battle.id}`}
        aria-label="Delete from my archive"
        title="Delete from my archive (other contestant's view is unaffected)"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function SiloChip({ silo, winner, delta, align }) {
  if (!silo) return <div className="text-xs text-[#6B6A68] italic">(awaiting opponent)</div>;
  return (
    <div className={`flex flex-col ${align === "right" ? "items-end text-right" : "items-start"}`}>
      <div className="text-sm font-medium text-[#1C1B1A] flex items-center gap-1.5">
        {winner && <Trophy size={13} className="text-[#A89360]" />}
        {silo.site_name}
      </div>
      <div className="text-[11px] text-[#6B6A68]">
        Elo {silo.elo} · {silo.tier} · {silo.record?.wins || 0}W–{silo.record?.losses || 0}L
        {typeof delta === "number" && delta !== 0 && (
          <span className={delta > 0 ? "text-[#4A5D4E] ml-1" : "text-[#C16E5A] ml-1"}>
            {delta > 0 ? `+${delta}` : delta}
          </span>
        )}
      </div>
    </div>
  );
}
